import React, { useState } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scoreColor(n) {
  if (n >= 8) return "text-emerald-300";
  if (n >= 5) return "text-amber-300";
  return "text-white/50";
}

const READ_SECTIONS = [
  ["creative_potential", "Creative potential"],
  ["budget_realism", "Budget realism"],
  ["recommended_direction", "Recommended direction"],
  ["production_complexity", "Production complexity"],
];

const READ_LISTS = [
  ["risks", "Risks"],
  ["upsells", "Upsell opportunities"],
  ["references", "Worth studying"],
];

// Ida's private strategic briefing. HQ-only, never shown to clients.
export default function IdaReadPanel({ project, onRead }) {
  const [refreshing, setRefreshing] = useState(false);
  const read = project.ida_read;

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/hq/projects/${project.id}/read/refresh`);
      onRead(data.ida_read);
    } catch (e) {
      console.error("read refresh failed", e);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#3b568f]/40 bg-[#16223a] p-5 md:p-6" data-testid="hq-ida-read">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8fb0e8]">Ida&rsquo;s Read · Private</div>
        <button
          type="button"
          onClick={refresh}
          className="text-white/30 hover:text-white/70 transition-colors"
          aria-label="Refresh Ida's Read"
          data-testid="hq-read-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!read && (
        <p className="text-white/40 text-[13px] leading-[1.7]">
          {refreshing ? "Ida is reading the enquiry…" : "Ida hasn\u2019t written her read yet. Tap refresh to ask her."}
        </p>
      )}

      {read && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className={`font-display font-extrabold text-[44px] leading-none ${scoreColor(read.opportunity_score)}`} data-testid="hq-read-score">
              {read.opportunity_score}<span className="text-[15px] text-white/25 font-normal">/10</span>
            </div>
            <div className="flex-1">
              <p className="text-white text-[14px] leading-[1.55] font-medium">{read.one_liner}</p>
              {read.urgency && (
                <span className="inline-block mt-1.5 text-[10px] uppercase tracking-[0.1em] text-white/40 border border-white/15 rounded-full px-2 py-0.5">
                  {read.urgency} urgency
                </span>
              )}
            </div>
          </div>

          <div className="space-y-3.5 pt-2 border-t border-white/[0.07]">
            {READ_SECTIONS.map(([k, label]) =>
              read[k] ? (
                <div key={k}>
                  <div className="text-white/35 text-[10.5px] uppercase tracking-[0.1em] mb-1">{label}</div>
                  <p className="text-white/80 text-[13.5px] leading-[1.65]">{read[k]}</p>
                </div>
              ) : null
            )}
            {READ_LISTS.map(([k, label]) =>
              Array.isArray(read[k]) && read[k].length > 0 ? (
                <div key={k}>
                  <div className="text-white/35 text-[10.5px] uppercase tracking-[0.1em] mb-1">{label}</div>
                  {read[k].map((item, i) => (
                    <p key={i} className="text-white/70 text-[13px] leading-[1.6]">· {item}</p>
                  ))}
                </div>
              ) : null
            )}
            {read.next_action && (
              <div>
                <div className="text-white/35 text-[10.5px] uppercase tracking-[0.1em] mb-1">Next action</div>
                <p className="text-white text-[13.5px] leading-[1.6] font-medium">{read.next_action}</p>
              </div>
            )}
            {read.advice_to_div && (
              <div className="rounded-xl bg-[#0e1730] border border-[#3b568f]/30 p-3.5">
                <div className="text-[#8fb0e8] text-[10.5px] uppercase tracking-[0.1em] mb-1.5">For you, Div</div>
                <p className="text-white/85 text-[13.5px] leading-[1.65] italic">{read.advice_to_div}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
